/**
 * Show details for one Launch Job.
 */

// @todo: Migrate to 'Dialog.Alert' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alert} from '@wandb/weave/components/Alert';
import React, {useEffect, useState} from 'react';
import {Redirect} from 'react-router-dom';

import {Analytics} from '../../../services/analytics';
import {setDocumentTitle} from '../../../util/document';
import {cleanTabName} from '../../../util/string';
import {job as jobUrl} from '../../../util/urls';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {useJobDetails} from './hooks';
import * as S from './JobDetailContent.styles';
import {JobDetailHeader} from './JobDetailHeader';
import {JobDetailTabContents} from './JobDetailTabContents';
import {JobDetailTabs} from './JobDetailTabs';

type JobDetailContentProps = {
  entityName: string;
  projectName: string;
  jobId: string;
  jobVersion: string | undefined;
  jobTab: string;
};

export const JobDetailContent = ({
  entityName,
  projectName,
  jobId,
  jobVersion,
  jobTab,
}: JobDetailContentProps) => {
  const {job, loading, error} = useJobDetails(jobId);

  const pageTitle = job?.name
    ? `Job - ${job.name} - ${cleanTabName(jobTab)}`
    : 'Job';
  useEffect(() => {
    setDocumentTitle(pageTitle);
  }, [pageTitle]);

  const [isDescriptionHighlighted, setDescriptionHighlighted] = useState(false);
  const [runDrawerOpen, setRunDrawerOpen] = useState(false);
  const onLaunchClick = () => {
    setRunDrawerOpen(true);
  };

  const closeDrawer = () => {
    setRunDrawerOpen(false);
  };

  if (error) {
    return <Alert severity="error">Error fetching job details</Alert>;
  }
  if (loading || !job) {
    return <Loader active size="large" name="job-detail-loader" />;
  }

  const onDescriptionMoreClick = () => {
    setDescriptionHighlighted(true);
    setTimeout(() => {
      setDescriptionHighlighted(false);
    }, 2000);
  };

  if (!jobVersion && job.versionInfo.runTarget) {
    const urlParams = {
      entityName,
      projectName,
      jobId,
      jobVersion: job.versionInfo.runTarget,
      jobTab: 'runs',
    };
    return <Redirect to={jobUrl(urlParams)} />;
  }

  Analytics.track(`job detail content tab: ${jobTab}`, {
    jobId: job?.id,
    jobTab,
    entityName,
    location: 'job detail content',
  });

  return (
    <S.DetailsPanel>
      <S.DetailsMain>
        <JobDrawer
          open={runDrawerOpen}
          closeDrawer={closeDrawer}
          entityName={entityName}
          projectName={projectName}
          defaultJobId={jobId}
          defaultRunToClone={job.versionInfo.runTarget ?? ''}
        />
        <JobDetailHeader
          entityName={entityName}
          projectName={projectName}
          job={job}
          onLaunchClick={onLaunchClick}
          onDescriptionMoreClick={onDescriptionMoreClick}
        />
        <JobDetailTabs
          entityName={entityName}
          projectName={projectName}
          jobId={jobId}
          jobVersion={jobVersion}
          jobTab={jobTab}
        />
        <JobDetailTabContents
          entityName={entityName}
          projectName={projectName}
          job={job}
          jobVersion={jobVersion}
          jobTab={jobTab}
          isDescriptionHighlighted={isDescriptionHighlighted}
        />
      </S.DetailsMain>
    </S.DetailsPanel>
  );
};
